import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Row, Col, Form, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserEdit, faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import CataractDetailsCard from './CataractDetailsCard';
import GlaucomaDetailsCard from './GlaucomaDetailsCard';
import DiabeticRetinopathyDetailsCard from './DiabeticRetinopathyDetailsCard';
import OtherConditionsDetailsCard from './OtherConditionsDetailsCard';
import AppNav from '../../../header/AppNav';
import AlertModal from '../../../components/AlertModal';
import { PatientServices } from '../../../services/PatientService';
import { AuthService } from '../../../services/AuthService';

const UpdatePatientPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  
  // Get pdType from URL search params
  const [searchParams] = useSearchParams();
  const pdType = searchParams.get('pdType');
  const [patientDetails, setPatientDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [diagnosis, setDiagnosis] = useState({
    cataract: false,
    glaucoma: false,
    diabeticRetinopathy: false,
    otherConditions: false
  });





  // Alert Modal state
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [modalData, setModalData] = useState({
    title: '',
    message: '',
    variant: 'info'
  });

  // Get user data for hospital ID
  const user = AuthService.getAuthEntity();
  const roleDetails = AuthService.getUserRole();
  const userRole = roleDetails?.role;
  const hospitalId = userRole === 'Hospital' ? user?.id : null;
  // Helper function to show alert modal
  const showAlert = (title, message, variant = 'info') => {
    setModalData({ title, message, variant });
    setShowAlertModal(true);
  };

  useEffect(() => {
    const fetchPatientDetails = async () => {
      try {
        setLoading(true);
        const response = await PatientServices.getPatientDetails(patientId);
        if (response && response.data) {
          setPatientDetails(response.data);
          // Initialize diagnosis based on existing complaints
          if (response.data.complaints) {
            setDiagnosis({
              cataract: !!response.data.complaints.cataractType,
              glaucoma: !!response.data.complaints.glaucomaType,
              diabeticRetinopathy: !!response.data.complaints.drType,
              otherConditions: !!response.data.complaints.ocDiagnosis
            });
          }
        } else {
          setError('Patient details not found');
          showAlert('Error', 'Patient details not found', 'danger');
        }
      } catch (error) {
        console.error('Error fetching patient details:', error);
        setError('Failed to load patient details. Please try again.');
        showAlert('Error', 'Failed to load patient details. Please try again.', 'danger');
      } finally {
        setLoading(false);
      }
    };

    if (patientId) {
      fetchPatientDetails();
    }
  }, [patientId, pdType]);

  const formatAge = (age) => {
    if (!age) return 'N/A';
    return `${age} years`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString;
    }
  };

  // Convert date from dd-MM-yyyy to yyyy-MM-dd for API
  const convertToApiDateFormat = (dateString) => {
    if (!dateString) return null;
    try {
      // If already in yyyy-MM-dd format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      // If in dd-MM-yyyy format, convert to yyyy-MM-dd
      if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
        const [day, month, year] = dateString.split('-');
        return `${year}-${month}-${day}`;
      }
      // Try parsing as date object
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const handleDiagnosisChange = (diagnosisType) => {
    setDiagnosis(prev => ({
      ...prev,
      [diagnosisType]: !prev[diagnosisType]
    }));
  };

  const handleGoBack = () => {
    navigate('/patients-history');
  };


  if (loading) {
    return (
      <div>
        <AppNav />
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="text-center">
            <Spinner animation="border" role="status" style={{ color: '#083270' }}>
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-3">Loading patient details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <AppNav />
        <div className="container mt-4">
          <Alert variant="danger">
            <h4>Error</h4>
            <p>{error}</p>
            <Button variant="primary" onClick={handleGoBack}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Back to Patients History
            </Button>
          </Alert>
        </div>
      </div>
    );
  }

  if (!patientDetails) {
    return (
      <div>
        <AppNav />
        <div className="container mt-4">
          <Alert variant="warning">
            <h4>Patient Not Found</h4>
            <p>The requested patient details could not be found.</p>
            <Button variant="primary" onClick={handleGoBack}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Back to Patients History
            </Button>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div>
      <AppNav />
      <div className="container-fluid mt-4" style={{ padding: '0 2rem' }}>
        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faUserEdit} size="2x" className="me-3" style={{ color: '#083270' }} />
            <div>
              <h2 className="mb-0" style={{ color: '#083270' }}>Update Patient Details</h2>
            </div>
          </div>
          <Button variant="outline-primary" onClick={handleGoBack}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Patients History
          </Button>
        </div>

        {/* Basic Information Section */}
        <Card className="mb-4" style={{ borderTop: '3px solid #083270' }}>
          <Card.Header className="bg-light">
            <Row className="align-items-center">
              <Col>
                <h5 className="mb-0" style={{ color: '#083270' }}>
                  Basic Information
                </h5>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={4}>
                <p className="mb-0">
                  <strong>Patient Name:</strong>
                  <span className="ms-2">{patientDetails.name || 'N/A'}</span>
                </p>
              </Col>
              <Col md={4}>
                <p className="mb-0">
                  <strong>Gender: </strong>
                    {patientDetails.gender || 'N/A'}
                </p>
              </Col>
              <Col md={4}>
                <p className="mb-0">
                  <strong>Age: </strong>
                  <span className="ms-2">{formatAge(patientDetails.age)}</span>
                </p>
              </Col>
            </Row>
            <Row className="mb-3">
            <Col md={4}>
                <p className="mb-0">
                  <strong>Patient ID: </strong>
                  {patientDetails.patientId || 'N/A'}
                </p>
              </Col>
              <Col md={4}>
                <p className="mb-0">
                  <strong>Mobile Number: </strong>
                  <span className="ms-2">{patientDetails.mobileNo || 'N/A'}</span>
                </p>
              </Col>
              <Col md={4}>
                <p className="mb-0">
                  <strong>Adhar Number: </strong>
                  <span className="ms-2">{patientDetails.idNumber || 'N/A'}</span>
                </p>
              </Col>
            </Row>
            <Row className="mb-3">
            <Col md={4}>
                <p className="mb-0">
                  <strong>Insurance:</strong>
                  <span className="ms-2">{patientDetails.insurance || 'N/A'}</span>
                </p>
              </Col>
              <Col md={6}>
                <p className="mb-0">
                  <strong>Address:</strong>
                  <span className="ms-2">{patientDetails.address || patientDetails.village || 'N/A'}</span>
                </p>
              </Col>
            </Row>
            <Row className="mb-3">
            <Col md={4}>
                <p className="mb-0">
                  <strong>Asha Name:</strong>
                  <span className="ms-2">{patientDetails.ashaDetails?.name || 'N/A'}</span>
                </p>
              </Col>
              <Col md={6}>
                <p className="mb-0">
                  <strong>Asha Block:</strong>
                  <span className="ms-2">{patientDetails.ashaBlockDetails?.ashaBlock || 'N/A'}</span>
                </p>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        {/* Cataract Details Card */}
        {pdType === 'cataract' && (
          <CataractDetailsCard
            patientId={patientId}
            patientDetails={patientDetails}
            hospitalId={hospitalId}
            showAlert={showAlert}
            formatDate={formatDate}
            convertToApiDateFormat={convertToApiDateFormat}
          />
        )}

        {/* Glaucoma Details Card */}
        {pdType === 'glaucoma' && (
          <GlaucomaDetailsCard
            patientId={patientId}
            patientDetails={patientDetails}
            hospitalId={hospitalId}
            showAlert={showAlert}
            formatDate={formatDate}
            convertToApiDateFormat={convertToApiDateFormat}
          />
        )}

        {/* Diabetic Retinopathy Details Card */}
        {pdType === 'dr' && (
          <DiabeticRetinopathyDetailsCard
            patientId={patientId}
            patientDetails={patientDetails}
            hospitalId={hospitalId}
            showAlert={showAlert}
            formatDate={formatDate}
            convertToApiDateFormat={convertToApiDateFormat}
          />
        )}

        {/* Other Conditions Details Card */}
        {pdType === 'other' && (
          <OtherConditionsDetailsCard
            patientId={patientId}
            patientDetails={patientDetails}
            hospitalId={hospitalId}
            showAlert={showAlert}
          />
        )}
      </div>
      
      {/* Alert Modal */}
      <AlertModal
        show={showAlertModal}
        onHide={() => setShowAlertModal(false)}
        title={modalData.title}
        message={modalData.message}
        variant={modalData.variant}
      />
    </div>
  );
};

export default UpdatePatientPage;
